"use client";

import { forwardRef } from "react";
import shared from "./shared";
import styled from "styled-components";
import { accentColorPrimary, grey3, grey5, getAppearanceConstant, setAppearanceConstantLocal } from "@churchofjesuschrist/eden-style-constants";
import { adjustHexAlpha } from "@churchofjesuschrist/eden-utilities";
import { jsx as _jsx } from "react/jsx-runtime";
const accentColorPrimaryModeStyle = getAppearanceConstant(accentColorPrimary);

// get dark value from response
const darkRootColor = accentColorPrimaryModeStyle.dark;
const [buttonGhostHoverDef, buttonGhostHover] = setAppearanceConstantLocal("button-ghost-hover", {
  light: grey3,
  dark: adjustHexAlpha(darkRootColor, 0.16),
  prefix: "eden"
});
const [buttonGhostActiveDef, buttonGhostActive] = setAppearanceConstantLocal("button-ghost-active", {
  light: grey5,
  dark: adjustHexAlpha(darkRootColor, 0.24),
  prefix: "eden"
});
const Component = styled.button.withConfig({
  componentId: "sc-7932sm-0"
})(["", ";", ";", ";background:none;color:", ";@media (any-hover:hover){&:hover{background:", ";color:", ";}}&:active,&[aria-pressed=\"true\"]{background:", ";}&[disabled],&[disabled]:hover{background:none;}&:is(a):visited:not(:active){color:", ";}"], shared, buttonGhostHoverDef, buttonGhostActiveDef, accentColorPrimary, buttonGhostHover, accentColorPrimary, buttonGhostActive, accentColorPrimary);

/**
 * `Ghost` buttons are subservient and supportive to a `Primary` and `Secondary` button. These should be used to indicate something that is optional or unessential for a user to complete a specific task.
 * @param {object} props - The component props
 * @param {React.ReactNode} props.children - The button contents.
 * @param {boolean} [props.disabled] - If the button should be disabled.
 * @param {boolean} [props.fullWidth] - If the button should expand to the full width of its parent.
 * @param {string} [props.href] - The link that the button should navigate to on click.
 * @param {React.ElementType} [props.renderAs] - The HTML tag string or React component to render as.
 * @param {boolean} [props.small] - Whether to apply smaller styles.
 * @param {string} [props.type] - The [type of the button](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#Attributes) or the [type of the link](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#Attributes).
 * @param {boolean} [props."aria-pressed"] - When present, the button can act as a "toggle" button. The value indicates whether it is toggled on or not. Control this with your application state.
 * @param {React.Ref} ref - Ref forwarded to the underlying element.
 * @returns {React.ReactElement} The Ghost component
 */
const GhostComponent = ({
  href,
  disabled,
  renderAs = href && !disabled ? "a" : "button",
  type = renderAs === "button" ? "button" : undefined,
  small,
  fullWidth,
  ...props
}, ref) => /*#__PURE__*/_jsx(Component, {
  ...props,
  ref: ref,
  type: type,
  href: href,
  disabled: disabled,
  $fullWidth: fullWidth,
  $small: small,
  as: renderAs,
  children: props.children
});
const Ghost = /*#__PURE__*/forwardRef(GhostComponent);
Ghost.displayName = "Ghost";
export default Ghost;